import React, { useEffect, useRef, useState } from 'react';
import { CalendarPlus, ChevronDown, Download, Settings, ShieldAlert } from 'lucide-react';
import { circleColorPalette } from './colors';

const usePopover = (resetKey) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    setOpen(false);
  }, [resetKey]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return { open, setOpen, ref };
};

export const MEETING_TYPES = [
  { type: 'tactique', label: 'Tactique', hint: 'Avancement des projets, points à aborder, actions à lancer.' },
  { type: 'gouvernance', label: 'Gouvernance', hint: 'Faire évoluer les rôles et le cercle à partir des tensions.' },
  { type: 'pilotage', label: 'Pilotage', hint: 'Revue projet par projet : jalons, objectif de la semaine, priorités.' },
];

export const MeetingMenu = ({ circle, openMeeting, loading, onOpenMeeting, onJoinMeeting }) => {
  const { open, setOpen, ref } = usePopover(circle?.id);
  if (openMeeting) {
    const label = MEETING_TYPES.find((m) => m.type === openMeeting.type)?.label;
    return (
      <button
        type="button"
        onClick={() => onJoinMeeting && onJoinMeeting(circle)}
        disabled={loading}
        className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-60"
      >
        <span className="h-2 w-2 rounded-full bg-white/90 animate-pulse" />
        Rejoindre la réunion{label ? ` ${label.toLowerCase()}` : ''}
      </button>
    );
  }
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={loading}
        className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg hover:bg-emerald-100 flex items-center gap-1.5 disabled:opacity-60"
      >
        <CalendarPlus size={14} />
        Lancer une réunion
        <ChevronDown size={13} />
      </button>
      {open && (
        // Ouvert vers la droite : le bouton est au bord gauche du panneau, qui couperait un menu ouvert vers la gauche.
        <div className="absolute left-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white shadow-lg z-20 p-1">
          {MEETING_TYPES.map((meeting) => (
            <button
              key={meeting.type}
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenMeeting && onOpenMeeting(meeting.type, circle);
              }}
              className="w-full text-left rounded-lg px-3 py-2 hover:bg-slate-50"
            >
              <span className="block text-sm font-semibold text-slate-800">{meeting.label}</span>
              <span className="block text-xs text-slate-500">{meeting.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const SettingsMenu = ({
  circle,
  voteTimeoutDays,
  showVotes = true,
  color,
  canEditVoteTimeout,
  canEditColor,
  onSavePatch,
  isSuperEditor,
  superEditMode,
  onToggleSuperEdit,
  onExportOrganization,
  organizationExporting,
}) => {
  const { open, setOpen, ref } = usePopover(circle?.id);
  const [days, setDays] = useState(String(voteTimeoutDays || 3));
  useEffect(() => {
    setDays(String(voteTimeoutDays || 3));
  }, [voteTimeoutDays, open]);

  const saveDays = () => {
    const parsed = Math.max(1, Math.round(Number(days) || 0));
    if (!parsed || parsed === Number(voteTimeoutDays)) {
      setDays(String(voteTimeoutDays || 3));
      return;
    }
    onSavePatch({ vote_timeout_days: parsed });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`p-2 rounded-lg border ${open ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-white text-slate-400 border-slate-200 hover:text-slate-600'}`}
        title="Réglages du cercle"
        aria-label="Réglages du cercle"
      >
        <Settings size={15} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg z-20 p-4 space-y-4">
          {showVotes && (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-slate-800">Votes hors réunion</p>
            <p className="text-xs text-slate-500">
              Sans réponse d'un membre, son vote compte comme « oui » au bout de ce délai.
            </p>
            {canEditVoteTimeout ? (
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="number"
                  min="1"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  onBlur={saveDays}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveDays();
                    }
                  }}
                  className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none"
                />
                <span className="text-xs text-slate-500">jour(s)</span>
              </div>
            ) : (
              <p className="text-sm text-slate-700">{voteTimeoutDays || 3} jour(s)</p>
            )}
          </div>
          )}
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-slate-800">Couleur sur la carte</p>
            {canEditColor ? (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => color && onSavePatch({ color: null })}
                  className={`px-2 py-1 text-[11px] rounded-full border ${color ? 'border-slate-200 text-slate-500' : 'border-slate-500 text-slate-700'}`}
                >
                  Auto
                </button>
                {circleColorPalette.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => option.value !== color && onSavePatch({ color: option.value })}
                    className={`h-6 w-6 rounded-full border ${option.value === color ? 'border-slate-600 ring-2 ring-slate-300' : 'border-white'}`}
                    style={{ backgroundColor: option.value }}
                    title={option.label}
                    aria-label={option.label}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                {color ? circleColorPalette.find((c) => c.value === color)?.label || color : 'Automatique'} — réglée par le leader du cercle.
              </p>
            )}
          </div>
          {isSuperEditor && (
            <div className="space-y-1 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onToggleSuperEdit();
                }}
                className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold border ${
                  superEditMode ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <ShieldAlert size={14} />
                {superEditMode ? 'Quitter la super édition' : 'Super édition (administrateur)'}
              </button>
              <p className="text-[11px] text-slate-400">Modifier la structure directement, sans passer par la gouvernance.</p>
            </div>
          )}
          {onExportOrganization && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onExportOrganization();
              }}
              disabled={organizationExporting}
              className="w-full flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              <Download size={14} />
              {organizationExporting ? 'Export en cours…' : "Exporter l'organisation (JSON)"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
